/* import __COLOCATED_TEMPLATE__ from './sq-field-date.hbs'; */
/**
## Field Date
`sq-field-date` is a "field"-styled calendar date selector intended to be nested inside a Form Field block component.
The [sq-input-calendar](/components/glass/sq-input-calendar.html) component is used to render the calendar within this component.

```example
@title Field Date Example

@controller-properties
{
  date: new Date()
}

@hbs
{{#sq-form-field label=(t "customers.marketing.new.campaignScheduleModal.date")}}
  {{sq-field-date value=date
                  valueFormatted=(full-day-year date)
                  placeholder=(t "fieldDate.selectDate")
                  onChange=(action (mut date))}}
{{/sq-form-field}}
```

### Parameters
@param {Date} [value=null] - The initial date value.
@param {String} [valueFormatted=null] - The formatted date string to be displayed.
@param {String} [placeholder=null] - The placeholder string to be displayed when when value is null.
@param {action} onChange - Fired whenever the date is changed on the calendar. Returns the selected date as a parameter.
@param {action} onToggleCalendar - Fired whenever the calendar is opened or closed.
@param {boolean} [enablePastDates=false] - Allows past dates to be selected by the user.
@param {boolean} [enableFutureDates=true] - Allows future dates to be selected by the user.
@param {Boolean} [disabled=false] - Disabled state for select.
@param {Object} disabledRange Object with beginDate and endDate values. This range of dates won't be able to be selected.

Sass dependencies at: `app/assets/stylesheets/bridgeport/components/form-elements/formfield/_formfield-date.sass`
*/

import { or } from '@ember/object/computed';
import Component from '@ember/component';
import FieldElementSupport from '@square/glass-ui/utils/field-element-support';
import { run } from '@ember/runloop';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend(FieldElementSupport, {
  // passed in
  disabledRange: null,
  placeholder: null,
  value: null,
  valueFormatted: null,
  constrainCalendarInParent: true,
  elementHasFocus: false,
  enablePastDates: false,
  enableFutureDates: true,
  shouldShowCalendar: false,
  isFocused: or('elementHasFocus', 'shouldShowCalendar'),
  boundClickToggle: null,
  // saved ref to event handler for easy removal

  // Make a mutable copy of the date for the calendar input to manipulate,
  // but so that we can make this component DDAU.
  mutableDateCopy: null,
  didInsertElement(...args) {
    this._super(...args);
    this.boundClickToggle = this.clickToggle.bind(this);
    window.addEventListener('click', this.boundClickToggle);
  },
  willDestroyElement(...args) {
    this._super(...args);
    window.removeEventListener('click', this.boundClickToggle);
  },
  didReceiveAttrs() {
    this._super();
    this.set('mutableDateCopy', this.get('value') ? new Date(this.get('value')) : new Date());
  },
  actions: {
    toggleCalendar() {
      this.toggleProperty('shouldShowCalendar');
      if (this.onToggleCalendar) {
        this.onToggleCalendar(this.get('shouldShowCalendar'));
      }
    },
    selectDate(date) {
      if (this.onChange) {
        this.onChange(date);
      }
      this.send('toggleCalendar');
    }
  },
  clickToggle(e) {
    run(() => {
      if (this.element.contains(e.target) || !this.get('shouldShowCalendar')) {
        return;
      }
      this.send('toggleCalendar');
    });
  },
  focusIn() {
    this.set('elementHasFocus', true);
  },
  focusOut() {
    this.set('elementHasFocus', false);
  }
});